import * as Handlebars from 'handlebars';
import jwt_decode from 'jwt-decode';

import { createReducer, on } from '@ngrx/store';

import { AppState } from '../../../app.reducer';
import { IWorkspace } from '../../../modules/chat/interfaces/IWorkspace';
import {
  ChannelConsent,
  ChannelRoles,
  channelsServices,
  ChannelType,
  IChannel,
  IChannelFilter,
} from '../interfaces/IChannel';
import { IDealer } from '../interfaces/IDealer';
import { DeliveryStatusTypesOptions, IMessage } from '../interfaces/IMessage';
import { IMessageableEntity } from '../interfaces/IMessageableEntity';
import { IMessageTemplate } from '../interfaces/IMessageTemplate';
import { IStandardResponse } from '../interfaces/IStandardResponse';
import { ITag } from '../interfaces/ITag';
import { IVideoCallSettings } from '../interfaces/IVideoCallMember';
import * as actions from './chat.actions';

export interface IChatState {
  channels: IChannel[];
  channelsFilter: IChannelFilter;
  dealers: IDealer[];
  messagesFilter: {
    size: number;
  };
  selectedChannel: IChannel | null;
  selectedWorkSpace: IWorkspace | null;
  sidebarVisible: boolean;
  smartReplie: string | null;
  tags: ITag[];
  users: IMessageableEntity[];
  videoCall: IVideoCallSettings | null;
  workspaces: IWorkspace[];
  smartRepliesEnabled: boolean;
  canUseCache: boolean;
  inputPhoneNumber: string | null;
  unreadMessages: number;
  messagesTemplates: IMessageTemplate[];
  consentTemplates: IMessageTemplate[];
  standardResponses: IStandardResponse[];
  tokenAuthentication: string | null;
}

export interface AppStateWithChat extends AppState {
  chat: IChatState;
}

export const chatInitialState: IChatState = {
  workspaces: [],
  selectedWorkSpace: null,
  channels: [],
  selectedChannel: null,
  sidebarVisible: false,
  videoCall: null,
  users: [],
  tags: [],
  dealers: [],
  channelsFilter: {
    searchTerm: '',
    filters: [],
    size: 20,
    page: 1,
  },
  messagesFilter: {
    size: 20,
  },
  unreadMessages: 0,
  smartReplie: null,
  smartRepliesEnabled: true,
  canUseCache: false,
  inputPhoneNumber: null,
  messagesTemplates: [],
  standardResponses: [],
  consentTemplates: [
    {
      externalId: ChannelConsent.PENDING,
      id: null,
      bodyText: 'Customer currently has a pending opt in',
      category: null,
      language: null,
    },
  ],
  tokenAuthentication: null,
};

export const reducer = createReducer(
  chatInitialState,

  on(actions.unsetChat, (state) => ({
    ...state,
    workspaces: [],
    selectedWorkSpace: null,
    selectedChannel: null,
    sidebarVisible: false,
    messages: [],
    channels: [],
  })),

  // WORKSPACES SECTION

  on(actions.loadWorkspaces, (state, { workspaces }) => ({
    ...state,
    workspaces,
  })),

  on(actions.selectWorkspace, (state, { selectedWorkspace }) => {
    return {
      ...state,
      selectedWorkSpace: { ...selectedWorkspace },
      messages: [],
      dealers: [],
      videoCall: null,
      users: [],
      tags: [],
      selectedUsers: [],
      autoResponses: [],
      channelsFilter: { ...selectedWorkspace.channelsFilters },
      channels: [],
      selectedChannel: null,
    };
  }),

  // END OF WORKSPACES SECTION

  // CHANNELS SECTION

  on(actions.setChannels, (state, { channels, concat }) => {
    let newChannels = [...channels];
    if (channels.length) {
      const hash: {
        [key: string]: boolean;
      } = {};
      newChannels = newChannels.filter((current) => {
        const exists = !hash[current.uid];
        hash[current.uid] = true;
        return exists;
      });
    }
    const newState = { ...state, channels: concat ? [...state.channels, ...newChannels] : newChannels };
    return newState;
  }),
  on(actions.setNewChannel, (state, { channel }) => {
    const newState = {
      ...state,
    };
    newState.channels = [...state.channels, channel];
    return newState;
  }),
  on(actions.setAuthorizationTokenKey, (state, { key }) => {
    const newState = {
      ...state,
      tokenAuthentication: key,
    };
    return newState;
  }),

  on(actions.selectChannel, (state, { channel }) => {
    const features = channelsServices[channel.routing] || [];
    const newState = {
      ...state,
      selectedChannel: {
        ...channel,
        isMember:
          state.selectedWorkSpace?.id === 'internal_chat'
            ? (channel?.member?.role && channel?.member?.role !== ChannelRoles.UNDEFINED) ||
              channel.type === ChannelType.DIRECT_MESSAGE
            : true,
        features: features,
      },
      messages: channel?.messages ? [...channel.messages] : [],
    };
    newState.channels = [...state.channels].map((newChannel) => {
      if (newChannel.uid === channel.uid) {
        return channel;
      }
      return newChannel;
    });

    return newState;
  }),

  on(actions.setUsers, (state, { users }) => ({
    ...state,
    users,
  })),
  on(actions.updateUserOnUsers, (state, { id, displayName, profileImageURL }) => {
    if (!state.users) {
      return state;
    }
    return {
      ...state,
      users: state.users.map((user) =>
        user.uid === id
          ? {
              ...user,
              displayName,
              profileImageURL,
            }
          : user
      ),
    };
  }),
  on(actions.unsetChannel, (state) => ({
    ...state,
    selectedChannel: null,
  })),

  on(actions.pinChannel, (state, { pinned, channel }) => {
    let newState = setSelectedChannel(state, pinned, channel?.uid, 'pinned');
    newState = setChannels(newState, pinned, channel?.uid, 'pinned');
    return newState;
  }),

  on(actions.setBotMode, (state, { value, channel }) => {
    let newState = setSelectedChannel(state, value, channel?.uid, 'isBotMode');
    newState = setChannels(newState, value, channel?.uid, 'isBotMode');
    newState = setSelectedChannel(newState, !value, channel?.uid, 'enabled');
    newState = setChannels(newState, !value, channel?.uid, 'enabled');
    return newState;
  }),

  on(actions.setChannelInfo, (state, { displayName, channel }) => {
    let newState = setSelectedChannel(state, displayName, channel.uid, 'displayName');
    newState = setChannels(newState, displayName, channel.uid, 'displayName');
    return newState;
  }),

  on(actions.setChannelDisplayName, (state, { displayName, channel }) => {
    let newState = setSelectedChannel(state, displayName, channel.uid, 'displayName');
    newState = setChannels(newState, displayName, channel.uid, 'displayName');
    return newState;
  }),

  on(actions.setChannelPhone, (state, { phoneNumber, channel }) => {
    let newState = setSelectedChannel(state, phoneNumber, channel.uid, 'phoneNumber');
    newState = setChannels(newState, phoneNumber, channel.uid, 'phoneNumber');
    return newState;
  }),

  on(actions.setSmartRepliesStatus, (state, { value, channel }) => {
    let newState = setSelectedChannel(state, value, channel.uid, 'hideSmartReplies');
    newState = setChannels(newState, value, channel.uid, 'hideSmartReplies');
    return newState;
  }),

  on(actions.muteChannel, (state, { mute, channel }) => {
    let newState = setSelectedChannel(state, mute, channel?.uid, 'isMuted');
    newState = setChannels(newState, mute, channel?.uid, 'isMuted');
    return newState;
  }),

  on(actions.setUnreadMessages, (state, { count }) => {
    return { ...state, unreadMessages: count };
  }),

  on(actions.setChannelStatus, (state, { value, channel }) => {
    let newState = setSelectedChannel(state, value, channel?.uid, 'status');
    newState = setChannels(newState, value, channel?.uid, 'status');
    return newState;
  }),

  on(actions.setChannelTranslationSetting, (state, { channelUID, value }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'hideTranslateSetting');
    newState = setChannels(newState, value, channelUID, 'hideTranslateSetting');
    return newState;
  }),

  on(actions.setChannelMessagesEndDate, (state, { channel, value }) => {
    let newState = setSelectedChannel(state, value, channel.uid, 'messagesEndDate');
    newState = setChannels(newState, value, channel.uid, 'messagesEndDate');
    return newState;
  }),

  on(actions.changeMemberCountInChannel, (state, { channelId, value }) => {
    let newState = setSelectedChannel(state, value, channelId, 'totalMembersCount');
    newState = setChannels(newState, value, channelId, 'totalMembersCount');
    return newState;
  }),

  on(actions.changeMembersInChannel, (state, { channelId, members }) => {
    const channelMembers = members as IMessageableEntity[];
    const currentChannelId = channelId as string;
    let newState = setSelectedChannel(state, channelMembers, currentChannelId, 'members');
    newState = setChannels(newState, channelMembers, currentChannelId, 'members');
    return newState;
  }),

  on(actions.setChannelMembersPhotos, (state, { members, channelUID }) => {
    let newState = setSelectedChannel(state, members, channelUID, 'members');
    newState = setChannels(newState, members, channelUID, 'members');
    const channel = newState.channels.find((e) => e.uid === channelUID);
    if (channel?.type !== ChannelType.DIRECT_MESSAGE && channel?.messages?.length) {
      const messages: IMessage[] = [...channel.messages].map((message) => {
        const newMessage = { ...message };
        if (channel.members?.length) {
          const member = channel.members.find(
            (e) =>
              e.uid === message.createdBy?.uid ||
              e.businessReference?.entityUID === message.createdBy?.businessReference?.entityUID ||
              e.businessReference?.entityUID === message.autor?.id
          );
          if (member) {
            newMessage.autor = {
              ...(newMessage.autor ?? {
                displayName: member.displayName,
                id: member.uid,
              }),
              profileImageURL: member.profileImageURL || newMessage.autor?.profileImageURL,
            };
          }
        }
        return newMessage;
      });
      newState = setSelectedChannel(newState, messages, channelUID, 'messages');
      newState = setChannels(newState, messages, channelUID, 'messages');
    }
    return newState;
  }),

  on(actions.setLoadingMessages, (state, { value, channelUID }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'isLoadingMessages');
    newState = setChannels(newState, value, channelUID, 'isLoadingMessages');
    return newState;
  }),

  on(actions.setLoadingOldMessages, (state, { value, channelUID }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'isLoadingOldMessages');
    newState = setChannels(newState, value, channelUID, 'isLoadingOldMessages');
    return newState;
  }),

  on(actions.setChannelOwner, (state, { owner, channel, tags }) => {
    let newState = setSelectedChannel(state, owner, channel.uid, 'owner');
    newState = setChannels(newState, owner, channel.uid, 'owner');
    newState = setSelectedChannel(newState, tags, channel.uid, 'systemTags');
    newState = setChannels(newState, tags, channel.uid, 'systemTags');
    return newState;
  }),

  on(actions.setChannelOrderId, (state, { orderId, channel }) => {
    const systemTags = [...(channel.systemTags ?? [])];
    const indexOrderId = systemTags.findIndex((e) => e.includes('orderId'));
    if (indexOrderId !== -1) {
      systemTags[indexOrderId] = `orderId:${orderId}`;
    } else {
      systemTags.push(`orderId:${orderId}`);
    }
    let newState = setSelectedChannel(state, systemTags, channel.uid, 'systemTags');
    newState = setChannels(newState, systemTags, channel.uid, 'systemTags');
    return newState;
  }),

  on(actions.deleteChannel, (state, { uid }) => {
    const newState = {
      ...state,
    };
    newState.channels = state.channels.filter((channelElement) => channelElement.uid !== uid);
    return newState;
  }),

  on(actions.changeOPTOut, (state, { optOut }) => {
    if (!state.selectedChannel) {
      return state;
    }
    return {
      ...state,
      selectedChannel: {
        ...state.selectedChannel,
        consent: {
          optIn: state.selectedChannel.consent?.optIn ?? false,
          optOut,
        },
      },
    };
  }),

  on(actions.setInputPhoneNumber, (state, { phoneNumber }) => ({
    ...state,
    inputPhoneNumber: phoneNumber,
  })),
  on(actions.changeOPTIn, (state, { optIn }) => {
    if (!state.selectedChannel) {
      return state;
    }
    return {
      ...state,
      selectedChannel: {
        ...state.selectedChannel,
        consent: {
          optIn,
          optOut: state.selectedChannel.consent?.optOut ?? false,
        },
      },
    };
  }),

  on(actions.setChannelLang, (state, { channelUID, lang }) => {
    let newState = setSelectedChannel(state, lang, channelUID, 'lang');
    newState = setChannels(newState, lang, channelUID, 'lang');
    return newState;
  }),

  // END OF CHANNELS SECTION

  //MESSAGES

  on(actions.setMessages, (state, { messages, concat, currentChannel }) => {
    const newMessages = concat ? [...messages, ...currentChannel.messages] : messages;
    let newState = setSelectedChannel(state, newMessages, currentChannel.uid, 'messages');
    newState = setChannels(newState, newMessages, currentChannel.uid, 'messages');
    return newState;
  }),

  on(actions.setLastMessage, (state, { channel, message }) => {
    let newChannel = state.channels.find((e) => e.uid === channel.uid);
    if (!newChannel && channel?.uid === state?.selectedChannel?.uid) {
      newChannel = { ...state?.selectedChannel };
    }
    if (!newChannel) {
      return { ...state };
    }
    if (!message.outcoming) {
      const actualDate = new Date();
      newChannel = { ...newChannel, expireAt: new Date(actualDate.getTime() + 24 * 60 * 60 * 1000) };
    }
    const messages = [...newChannel.messages];
    const index = newChannel.messages.map((e) => e.uid).indexOf(message.uid);
    if (index === -1) {
      messages.push(message);
      let newState = setSelectedChannel(state, messages, channel.uid, 'messages');
      newState = setChannels(newState, messages, channel.uid, 'messages');
      newState = setChannels(newState, message, channel.uid, 'lastMessage');
      newState = setSelectedChannel(newState, message, channel.uid, 'lastMessage');
      if (!message.outcoming) {
        const unreadCount = (newChannel.unreadMessages ?? 0) + 1;
        newState = setChannels(newState, unreadCount, channel.uid, 'unreadMessages');
        newState = setSelectedChannel(newState, unreadCount, channel.uid, 'unreadMessages');
        if (!newChannel.unreadMessages) {
          newState = { ...newState, unreadMessages: newState.unreadMessages + 1 };
        }
      }
      return newState;
    } else {
      return { ...state };
    }
  }),

  on(actions.setCompletedMessagesChannel, (state, { channelUID, value }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'messagesCompleted');
    newState = setChannels(newState, value, channelUID, 'messagesCompleted');
    return newState;
  }),

  on(actions.leaveChannelMember, (state, { channelUID }) => {
    const newState = {
      ...state,
    };
    if (channelUID === state?.selectedChannel?.uid) {
      newState.selectedChannel = {
        ...state.selectedChannel,
        messages: [] as IMessage[],
        isMember: false,
        member: { ...state.selectedChannel.member, role: ChannelRoles.UNDEFINED },
      };
    }
    newState.channels = [...state.channels].map((channelElement) => {
      const newChannel = { ...channelElement };
      if (newChannel.uid == channelUID) {
        newChannel.messages = [] as IMessage[];
        newChannel.isMember = false;
        newChannel.member = { ...newChannel.member, role: ChannelRoles.UNDEFINED };
      }
      return newChannel;
    });
    return newState;
  }),

  on(actions.setReadChannel, (state, { channel, value }) => {
    let newState = setSelectedChannel(state, 0, channel?.uid, 'unreadMessages');
    newState = setSelectedChannel(newState, value, channel?.uid, 'unread');
    newState = setChannels(newState, 0, channel?.uid, 'unreadMessages');
    newState = setChannels(newState, value, channel?.uid, 'unread');
    return newState;
  }),

  on(actions.setUnreadChannelCount, (state, { channel, value }) => {
    if (value <= 0) {
      value = 0;
    }
    let newState = setSelectedChannel(state, value, channel?.uid, 'unreadMessages');
    newState = setChannels(newState, value, channel?.uid, 'unreadMessages');
    if (value === 0) {
      newState = setSelectedChannel(newState, false, channel?.uid, 'unread');
      newState = setChannels(newState, false, channel?.uid, 'unread');
    }
    return newState;
  }),

  on(actions.setAllChannelsRead, (state) => {
    const newState = { ...state };
    const currentUserId = getCurrentUserId();
    if (newState.channels && newState.channels.length > 0) {
      newState.channels = newState.channels.map((channel) => {
        if (currentUserId && channel.owner?.id === currentUserId) {
          return {
            ...channel,
            unread: false,
            unreadMessages: 0,
          };
        }
        return channel;
      });
    }
    if (newState.selectedChannel && currentUserId && newState.selectedChannel.owner?.id === currentUserId) {
      newState.selectedChannel = {
        ...newState.selectedChannel,
        unread: false,
        unreadMessages: 0,
      };
    }

    return newState;
  }),

  on(
    actions.setMessageDeliveryStatus,
    (state, { messageUID, newMessageUID, channelUID, errorMessage, status, attachments, body }) => {
      let newChannel = state.channels.find((e) => e.uid === channelUID);
      if (!newChannel) {
        if (state.selectedChannel?.uid === channelUID) {
          newChannel = { ...state.selectedChannel };
        }
      }
      if (!newChannel) {
        return { ...state };
      }
      const messages = [...newChannel.messages].map((e) => {
        if (e.uid === messageUID) {
          return {
            ...e,
            uid: newMessageUID ? newMessageUID : e.uid,
            attachments: attachments ? attachments : e.attachments,
            deliveryStatus: DeliveryStatusTypesOptions[status],
            body: body || e.body,
            errorMessage: errorMessage,
          };
        } else {
          return e;
        }
      });
      let newState = setSelectedChannel(state, messages, channelUID, 'messages');
      newState = setChannels(newState, messages, channelUID, 'messages');
      return newState;
    }
  ),

  on(actions.unsetMessage, (state, { messageUid, channelUid }) => {
    const newChannel = state.channels.find((e) => e.uid === channelUid);
    if (!newChannel) {
      return { ...state };
    }
    const messages = [...newChannel.messages].map((e) => {
      if (e.uid === messageUid) {
        return { ...e, isDeleted: true };
      } else {
        return e;
      }
    });
    let newState = setSelectedChannel(state, messages, channelUid, 'messages');
    newState = setChannels(newState, messages, channelUid, 'messages');
    return newState;
  }),

  on(actions.unsetMessages, (state) => {
    let newState = setSelectedChannel(state, [], state.selectedChannel?.uid, 'messages');
    newState = setChannels(newState, [], state.selectedChannel?.uid, 'messages');
    return newState;
  }),

  on(actions.setRepairOrder, (state, { message, repairOrder, video }) => {
    const newChannel = state.channels.find((e) => e.uid === state.selectedChannel?.uid);
    if (!newChannel) {
      return { ...state };
    }
    const messages = [...newChannel.messages].map((e) => {
      if (e.uid === message.uid) {
        return { ...e, repairOrder: { ...repairOrder, video } };
      } else {
        return e;
      }
    });
    let newState = setSelectedChannel(state, messages, state.selectedChannel?.uid, 'messages');
    newState = setChannels(newState, messages, state.selectedChannel?.uid, 'messages');
    return newState;
  }),

  on(actions.setMessageTranslationStatus, (state, { message, channel, status, error }) => {
    let newChannel = state.channels.find((e) => e.uid === channel?.uid);
    if (!newChannel) {
      if (state.selectedChannel?.uid === channel?.uid) {
        newChannel = { ...state.selectedChannel };
      }
    }
    if (!newChannel) {
      return { ...state };
    }
    const messages = [...newChannel.messages].map((e) => {
      if (e.uid === message.uid) {
        return { ...e, isLoadingTranslation: status, errorTranslation: error };
      } else {
        return e;
      }
    });
    let newState = setSelectedChannel(state, messages, channel.uid, 'messages');
    newState = setChannels(newState, messages, channel.uid, 'messages');
    return newState;
  }),

  on(actions.setMessageTranslated, (state, { message, translation, channelUID }) => {
    let newChannel = state.channels.find((e) => e.uid === channelUID);
    if (!newChannel) {
      if (state.selectedChannel?.uid === channelUID) {
        newChannel = { ...state.selectedChannel };
      }
    }
    if (!newChannel) {
      return { ...state };
    }
    const messages = [...newChannel.messages].map((e) => {
      if (e.uid === message.uid) {
        return { ...e, translation: translation };
      } else {
        return e;
      }
    });
    let newState = setSelectedChannel(state, messages, channelUID, 'messages');
    newState = setChannels(newState, messages, channelUID, 'messages');
    return newState;
  }),

  // END OF MESSAGES SECTION

  // USERS SECTION

  // END OF USERS SECTION

  on(actions.toggleSidebar, (state, { active }) => ({
    ...state,
    sidebarVisible: active,
  })),

  // DEALERS SECTION
  on(actions.setDealers, (state, { dealers }) => ({
    ...state,
    dealers: [...dealers],
  })),

  on(actions.unsetDealers, (state) => ({
    ...state,
    dealers: [],
  })),

  // END OF DEALERS SECTION

  // FILTERS SECTION
  on(actions.setSearchTerm, (state, { searchTerm }) => ({
    ...state,
    channelsFilter: { ...state.channelsFilter, searchTerm },
  })),

  on(actions.setChannelsFilter, (state, { filter }) => ({
    ...state,
    channelsFilter: { ...filter },
  })),

  on(actions.setFilterValue, (state, { value, key }) => {
    const filters = state.channelsFilter.filters.map((filter) => {
      return filter.name.toUpperCase() === key.toUpperCase() ? { ...filter, value: value } : filter;
    });
    return {
      ...state,
      channelsFilter: { ...state.channelsFilter, page: 1, filters: filters },
    };
  }),

  // END OF FILTER SECTION

  // VIDEO CALL SECTION

  on(actions.setVideoCall, (state, { videoCall }) => {
    return {
      ...state,
      videoCall: videoCall,
    };
  }),

  // END OF VIDEO CALL SECTION

  // TAGS SECTION

  on(actions.setTags, (state, { tags }) => {
    return {
      ...state,
      tags,
    };
  }),

  // END OF TAGS SECTION

  on(actions.setSmartReplieSelected, (state, { messages }) => {
    return {
      ...state,
      smartReplie: messages,
    };
  }),

  on(actions.setSmartReplies, (state, { smartReplies, channelUID }) => {
    let newState = setSelectedChannel(state, smartReplies, channelUID, 'smartReplies');
    newState = setChannels(newState, smartReplies, channelUID, 'smartReplies');
    return newState;
  }),

  on(actions.setLoadingSmartReplies, (state, { value, channelUID }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'isLoadingSmartReplies');
    newState = setChannels(newState, value, channelUID, 'isLoadingSmartReplies');
    return newState;
  }),

  on(actions.setLoadingStandardReplies, (state, { value, channelUID }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'isLoadingStandardResponses');
    newState = setChannels(newState, value, channelUID, 'isLoadingStandardResponses');
    return newState;
  }),

  on(actions.setStartMessagesOnChannel, (state, { value, channel }) => {
    const dateValue = value ?? undefined;
    let newState = setSelectedChannel(state, dateValue, channel.uid, 'messagesStartDate');
    newState = setChannels(newState, dateValue, channel.uid, 'messagesStartDate');
    return newState;
  }),
  on(actions.setEndMessagesOnChannel, (state, { value, channel }) => {
    const dateValue = value ?? undefined;
    let newState = setSelectedChannel(state, dateValue, channel.uid, 'messagesEndDate');
    newState = setChannels(newState, dateValue, channel.uid, 'messagesEndDate');
    return newState;
  }),
  on(actions.setStandardReplies, (state, { standardReplies, channel, selectedWorkspace, context }) => {
    const standardResponsesFiltered = standardReplies
      .filter((e) => e.category?.toUpperCase() === selectedWorkspace.id.toUpperCase())
      .map((e) => {
        const standardResponse = { ...e };
        const template = Handlebars.compile(standardResponse.message);
        standardResponse.message = template(context);
        return standardResponse;
      });

    let newState = setSelectedChannel(state, standardResponsesFiltered, channel?.uid, 'standardResponses');
    newState = setChannels(newState, standardResponsesFiltered, channel?.uid, 'standardResponses');
    newState = { ...newState, standardResponses: standardReplies };
    return newState;
  }),

  on(actions.setChannelStatusEnabled, (state, { value, channelUID }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'enabled');
    newState = setChannels(newState, value, channelUID, 'enabled');
    return newState;
  }),

  on(actions.setChannelExpirationDate, (state, { expirationDate, channelUID }) => {
    let newState = setSelectedChannel(state, expirationDate, channelUID, 'expireAt');
    newState = setChannels(newState, expirationDate, channelUID, 'expireAt');

    return newState;
  }),

  on(actions.setChannelOldMessagesValue, (state, { value, channel }) => {
    let newState = setSelectedChannel(state, value, channel.uid, 'hasOldMessagesToLoad');
    newState = setChannels(newState, value, channel.uid, 'hasOldMessagesToLoad');
    return newState;
  }),

  on(actions.setChannelConsent, (state, { value, channelUID }) => {
    let newState = setSelectedChannel(state, value, channelUID, 'consentStatus');
    newState = setChannels(newState, value, channelUID, 'consentStatus');
    return newState;
  }),

  on(actions.updateOwnerOnChannelsAndMessagesByID, (state, { ownerDisplayName, ownerID, ownerProfilePic }) => {
    let newState = { ...state };
    const updatedOwner = {
      id: ownerID,
      displayName: ownerDisplayName,
      profileImageURL: ownerProfilePic,
    };
    // for list of channels
    state.channels.forEach((channel) => {
      const updatedMessages = channel.messages.map((message) => {
        return message?.autor?.id == ownerID ? { ...message, autor: updatedOwner } : message;
      });
      if (JSON.stringify(updatedMessages) !== JSON.stringify(channel.messages)) {
        newState = setChannels(newState, updatedMessages, channel.uid, 'messages');
      }
      if (channel?.owner?.id == ownerID) {
        newState = setChannels(newState, updatedOwner, channel.uid, 'owner');
      }
    });

    // for selected channel
    if (newState.selectedChannel) {
      const selectedChannel = newState.selectedChannel;
      const updatedMessagesSelectedChannel = selectedChannel.messages.map((message) =>
        message?.autor?.id == ownerID ? { ...message, autor: updatedOwner } : message
      );
      newState = setSelectedChannel(newState, updatedMessagesSelectedChannel, selectedChannel.uid, 'messages');
      if (selectedChannel.owner?.id == ownerID) {
        newState = setSelectedChannel(newState, updatedOwner, selectedChannel.uid, 'owner');
      }
    }

    return newState;
  }),

  on(actions.setMessageReaction, actions.applyMessageReaction, (state, { reaction, channelUID, messageUID }) => {
    let channel = state.channels.find((e) => e.uid === channelUID);
    if (!channel && state.selectedChannel?.uid === channelUID) {
      channel = { ...state.selectedChannel };
    }
    if (!channel) {
      return { ...state };
    }
    const messages = channel.messages.map((message) => {
      if (message.uid === messageUID) {
        let reactions = [...(message?.reactions || [])];
        if (reactions?.length) {
          const indexReaction = reactions.map((e) => e.createdBy.uid).indexOf(reaction.createdBy.uid);
          if (indexReaction !== -1) {
            reactions[indexReaction] = reaction;
          } else {
            reactions.push(reaction);
          }
        } else {
          reactions = [reaction];
        }

        return { ...message, reactions: reactions };
      }
      return { ...message };
    });
    let newState = setSelectedChannel(state, messages, channelUID, 'messages');
    newState = setChannels(newState, messages, channelUID, 'messages');
    return { ...newState };
  }),
  on(actions.setSmartRepliesEnabled, (state, { value }) => ({ ...state, smartRepliesEnabled: value })),
  on(actions.setCanUseCache, (state, { value }) => ({ ...state, canUseCache: value })),
  on(actions.setMessagesTemplates, (state, { templates }) => ({
    ...state,
    messagesTemplates: [...state.messagesTemplates, ...templates],
  })),

  on(actions.setCompleteChatState, (state, { savedState }) => ({
    ...savedState,
  }))
);

function setSelectedChannel<T extends keyof IChannel>(
  state: IChatState,
  value: IChannel[T],
  channelUID: string | undefined,
  param: keyof IChannel
): IChatState {
  if (!channelUID || !state?.selectedChannel) {
    return { ...state };
  }
  const selectedChannel: IChannel = { ...state.selectedChannel };
  if (selectedChannel && selectedChannel.uid === channelUID) {
    selectedChannel[param as T] = value;
  }
  const newState: IChatState = {
    ...state,
    selectedChannel: selectedChannel,
  };
  return newState;
}

function setChannels<T extends keyof IChannel>(
  state: IChatState,
  value: IChannel[T],
  channelUID: string | undefined,
  param: keyof IChannel
): IChatState {
  if (!channelUID || !state?.channels?.length) {
    return { ...state };
  }
  const channels = [...state.channels].map((e) => {
    const channel = { ...e };
    if (e.uid === channelUID) {
      channel[param as T] = value;
    }
    return channel;
  }) as IChannel[];
  const newState: IChatState = {
    ...state,
    channels: channels,
  };
  return newState;
}

/**
 * Obtiene el ID del usuario actual desde el token almacenado
 * @returns El ID del usuario (subjectMessageableEntity.uid) o null si no está disponible
 */
function getCurrentUserId(): string | null {
  try {
    const messagingApiToken = localStorage.getItem('messaging-api-token');
    if (messagingApiToken) {
      const tokenDecoded = jwt_decode(messagingApiToken) as {
        subjectMessageableEntity?: { businessReference?: { entityUID: string } };
      };
      return tokenDecoded?.subjectMessageableEntity?.businessReference?.entityUID || null;
    }
    return null;
  } catch (error) {
    return null;
  }
}
